// THE MESSAGE RAIL — every game message in ONE place: bottom-centre, one at a time, never in the player's face.
//
//   • A message holds the rail for at least MessageRailConfig.minMs (5 s). A newer one waits in the queue, then BUMPS it: the old
//     card slides down off the screen and fades, the new one rises into the slot — a rolling flow of messages.
//   • Plain messages (`info`, `story`) leave by themselves after their life. A message that wants a CHOICE (`choice`: Repair,
//     FEED…) stays until the player picks or closes it — and if it is bumped (or closed) unacted it comes back later, between other
//     messages, every reappearMs, until it is resolved (resolveMessage(key)) or a choice with snoozeMs puts it off for longer.
//   • `urgent` messages (the Emperor's countdown, goons at the gates) jump the queue and are never bumped while they stand.
//   • Every card has a × (the house rule). A key makes a message unique: posting the same key again updates the card in place.
//
// Dev/test: window.__rail = { post, resolve, state(), config } — config is live (a test shortens the holds).

import { MessageRailConfig } from '../config/balance.js'
import { addCloseButton } from './domUtil.js'
import { Events, emit } from './eventBus.js'

const config = { ...MessageRailConfig }
let root = null, current = null, queue = [], seq = 0, timer = null
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
const now = () => Date.now()

function ensureRoot() {
  if (root && document.body.contains(root)) return root
  root = document.createElement('div')
  root.className = 'msg-rail'
  root.setAttribute('role', 'status'); root.setAttribute('aria-live', 'polite')
  document.body.appendChild(root)
  return root
}

// Post a message. Returns its id. Fields: key, kind ('info' | 'story' | 'choice' | 'urgent'), type (styling tag, e.g. DROUGHT),
// icon, title, text, action (muted line), choices [{ label, primary, disabled, onPick, snoozeMs }], meter { until, total },
// minMs, lifeMs (Infinity for choice/urgent), reappearMs (choice: comes back when bumped unacted), pinned.
export function postMessage(m) {
  const kind = m.kind ?? 'info'
  if (!m.title && !m.text) return 0 // (nothing to say: never a blank card)
  const msg = {
    id: ++seq, key: m.key ?? null, kind, type: m.type ?? '', icon: m.icon ?? '', title: m.title ?? '', text: m.text ?? '', action: m.action ?? '',
    html: !!m.html, choices: Array.isArray(m.choices) ? m.choices : [], meter: m.meter ?? null,
    minMs: m.minMs ?? config.minMs, minMsDefault: m.minMs == null ? config.minMs : null,
    lifeMs: m.lifeMs ?? (kind === 'story' ? config.storyMs : kind === 'choice' || kind === 'urgent' ? Infinity : config.infoMs),
    reappearMs: m.reappearMs ?? (kind === 'choice' ? config.reappearMs : 0),
    pinned: !!m.pinned || kind === 'urgent',
    postedAt: now(), notBefore: 0, shownAt: 0, acted: false, el: null, shows: 0,
  }
  if (msg.key) {
    const was = queue.find((q) => q.key === msg.key)
    queue = queue.filter((q) => q.key !== msg.key)
    if (was && !m.now) msg.notBefore = was.notBefore // (a re-post keeps its place in time — a snooze is not reset by an update; `now` asks for it at once)
    if (current && current.key === msg.key && !current.el?.classList.contains('leaving')) { // update the standing card in place
      Object.assign(current, { type: msg.type, icon: msg.icon, title: msg.title, text: msg.text, action: msg.action, html: msg.html, choices: msg.choices, meter: msg.meter, lifeMs: msg.lifeMs, reappearMs: msg.reappearMs, pinned: msg.pinned, kind })
      paint(current)
      return current.id
    }
  }
  // the same words twice (a burst of identical notices) say nothing new: coalesce
  const same = (q) => q.kind === msg.kind && q.title === msg.title && q.text === msg.text
  if (kind !== 'choice' && kind !== 'urgent') { const dup = queue.find(same) ?? (current && same(current) && !current.el?.classList.contains('leaving') ? current : null); if (dup) return dup.id }
  if (msg.pinned || m.now) queue.unshift(msg); else queue.push(msg)
  tick()
  return msg.id
}

// Drop everything, now and queued (dev/test isolation; a kingdom switch).
export function clearMessages() {
  for (const q of queue) q.el?.remove()
  queue = []
  if (current) { current.el?.remove(); current = null }
}

// The condition behind a keyed message is gone (repairs started, the Emperor appeased…): drop it, now and from the queue.
export function resolveMessage(key) {
  queue = queue.filter((q) => q.key !== key)
  if (current && current.key === key) leave(current, 'resolved')
  tick()
}

function build(msg) {
  const el = document.createElement('div')
  el.className = `msg msg--${msg.kind}${msg.choices.length ? ' msg--has-choices' : ''}`
  el.dataset.type = msg.type; el.dataset.key = msg.key ?? ''; el.dataset.id = String(msg.id)
  paint(msg, el)
  addCloseButton(el, () => close(msg))
  return el
}
function paint(msg, el = msg.el) {
  if (!el) return
  const body = msg.html ? msg.text : esc(msg.text)
  el.querySelector('.msg__inner')?.remove()
  const inner = document.createElement('div'); inner.className = 'msg__inner'
  inner.innerHTML = `
    ${msg.icon ? `<span class="msg__icon">${msg.icon}</span>` : ''}
    <div class="msg__body">
      ${msg.title ? `<div class="msg__title">${esc(msg.title)}</div>` : ''}
      ${msg.text ? `<div class="msg__text">${body}</div>` : ''}
      ${msg.action ? `<div class="msg__action">${esc(msg.action)}</div>` : ''}
      ${msg.meter ? '<div class="msg__meter"><span class="msg__meter-fill"></span></div>' : ''}
      ${msg.choices.length ? `<div class="msg__buttons">${msg.choices.map((c, i) => `<button type="button" class="msg__btn${c.primary ? ' msg__btn--primary' : ''}" data-choice="${i}"${c.disabled ? ' disabled' : ''}>${esc(c.label)}</button>`).join('')}</div>` : ''}
    </div>`
  inner.querySelectorAll('[data-choice]').forEach((b) => b.addEventListener('click', () => pick(msg, msg.choices[Number(b.dataset.choice)])))
  el.prepend(inner)
  el.className = `msg msg--${msg.kind}${msg.choices.length ? ' msg--has-choices' : ''}${el.classList.contains('leaving') ? ' leaving' : ''}`
  el.dataset.type = msg.type
  updateMeter(msg)
}
function updateMeter(msg) {
  if (!msg.meter || !msg.el) return
  const fill = msg.el.querySelector('.msg__meter-fill'); if (!fill) return
  const left = Math.max(0, msg.meter.until - now()), total = Math.max(1, msg.meter.total)
  fill.style.width = `${Math.max(0, Math.min(100, (left / total) * 100))}%`
}

function show(msg) {
  ensureRoot()
  msg.el = build(msg) // (it rises into the slot by a CSS animation — msgIn)
  root.appendChild(msg.el)
  msg.shownAt = now(); msg.shows += 1
  current = msg
  emit(Events.MessageShown, { key: msg.key, kind: msg.kind })
}
// The card goes: down and away. A choice left unmade comes back later (bumped, expired or closed), unless resolved meanwhile.
function leave(msg, why) {
  if (!msg.el || msg.el.classList.contains('leaving')) return
  const el = msg.el
  el.classList.add('leaving')
  setTimeout(() => el.remove(), config.slideMs)
  if (current === msg) current = null
  msg.el = null
  if (msg.kind === 'choice' && !msg.acted && why !== 'resolved' && why !== 'acted' && msg.reappearMs > 0) {
    msg.notBefore = now() + msg.reappearMs
    queue.push(msg)
  }
}
function close(msg) { leave(msg, 'closed'); tick() }
function pick(msg, choice) {
  if (!choice || choice.disabled) return
  try { choice.onPick?.() } catch (err) { console.warn('[rail] choice failed:', err) }
  if (choice.snoozeMs) { msg.notBefore = now() + choice.snoozeMs; leave(msg, 'snoozed'); queue.push(msg) } // "later": it comes back after the snooze
  else { msg.acted = true; leave(msg, 'acted') }
  tick()
}

const ready = () => queue.find((q) => q.notBefore <= now())
function tick() {
  const t = now()
  // a plain message that waited too long is stale: the log has it; the rail must not replay a busy minute for minutes
  queue = queue.filter((q) => q.kind === 'choice' || q.kind === 'urgent' || q.notBefore > t || t - q.postedAt <= config.staleMs)
  if (current) {
    updateMeter(current)
    if (t >= current.shownAt + current.lifeMs) leave(current, 'expired')
  }
  if (current) {
    const next = ready()
    const hold = current.minMs === current.minMsDefault ? config.minMs : current.minMs // (a message without its own hold follows the live config)
    if (next && ((next.pinned && !current.pinned) || (!current.pinned && t >= current.shownAt + hold))) leave(current, 'bumped')
  }
  if (!current) {
    const next = ready()
    if (next) { queue = queue.filter((q) => q !== next); show(next) }
  }
}
export function mountMessageRail() {
  ensureRoot()
  if (timer) clearInterval(timer)
  timer = setInterval(tick, config.tickMs)
  if (import.meta.env?.DEV) window.__rail = {
    post: postMessage, resolve: resolveMessage, clear: clearMessages, config,
    state: () => ({ current: current ? { id: current.id, key: current.key, kind: current.kind, title: current.title, shownAt: current.shownAt, shows: current.shows, leaving: !!current.el?.classList.contains('leaving') } : null, queue: queue.map((q) => ({ id: q.id, key: q.key, kind: q.kind, title: q.title, notBefore: q.notBefore, shows: q.shows })) }),
  }
}
